import React, { useState, useEffect } from 'react';
import styles from './BackendSettingsModal.module.css';
import { useAppContext } from '@/context/AppContext';

interface BackendSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: 'source' | 'destination';
}

const BackendSettingsModal: React.FC<BackendSettingsModalProps> = ({ isOpen, onClose, type }) => {
  // Get context values based on type
  const { 
    sourceBackend, 
    setSourceBackend, 
    sourcePort, 
    setSourcePort,
    isCustomSourceBackend,
    setIsCustomSourceBackend,
    destBackend, 
    setDestBackend, 
    destPort, 
    setDestPort,
    isCustomDestBackend,
    setIsCustomDestBackend,
    validateConnection,
    validateDestConnection
  } = useAppContext();

  // Local state for form values
  const [localBackend, setLocalBackend] = useState(type === 'source' ? sourceBackend : destBackend);
  const [localPort, setLocalPort] = useState(type === 'source' ? sourcePort : destPort);
  const [localIsCustom, setLocalIsCustom] = useState(type === 'source' ? isCustomSourceBackend : isCustomDestBackend);



  // Update local state when props change
  useEffect(() => {
    if (isOpen) {
      setLocalBackend(type === 'source' ? sourceBackend : destBackend);
      setLocalPort(type === 'source' ? sourcePort : destPort);
      setLocalIsCustom(type === 'source' ? isCustomSourceBackend : isCustomDestBackend);
    }
  }, [isOpen, type, sourceBackend, sourcePort, isCustomSourceBackend, destBackend, destPort, isCustomDestBackend]);

  // Handle backend change
  const handleBackendChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === 'custom') {
      setLocalIsCustom(true);
    } else {
      setLocalIsCustom(false);
      setLocalBackend(value);
    }
  };

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Check if settings have changed
    let settingsChanged = false;
    
    // Update global state
    if (type === 'source') {
      // Check if source settings have changed
      if (localBackend !== sourceBackend || 
          localPort !== sourcePort || 
          localIsCustom !== isCustomSourceBackend) {
        settingsChanged = true;
        
        // Update source settings
        setSourceBackend(localBackend);
        setSourcePort(localPort);
        setIsCustomSourceBackend(localIsCustom);
        
        // Validate source connection only if settings changed
        validateConnection();
      }
    } else {
      // Check if destination settings have changed
      if (localBackend !== destBackend || 
          localPort !== destPort || 
          localIsCustom !== isCustomDestBackend) {
        settingsChanged = true;
        
        // Update destination settings
        setDestBackend(localBackend);
        setDestPort(localPort);
        setIsCustomDestBackend(localIsCustom);
        
        // Validate destination connection only if settings changed
        validateDestConnection();
      }
    }
    
    // Log whether settings were changed
    if (settingsChanged) {
      console.log(`${type} backend settings updated, validating connection`);
    } else {
      console.log(`${type} backend settings unchanged, skipping validation`);
    }
    
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <h2 className={styles.modalTitle}>
          {type === 'source' ? 'Source Backend Settings' : 'Destination Backend Settings'}
        </h2>
        
        <form onSubmit={handleSubmit} className={styles.form}>
          
          <div className={styles.formGroup}>
            <label htmlFor="backend" className={styles.label}>Backend URL</label>
            <select
              id="backend"
              className={styles.select}
              value={localIsCustom ? 'custom' : localBackend}
              onChange={handleBackendChange}
            >
              <option value="tracking.cityflo.com">tracking.cityflo.com</option>
              <option value="tracking.cityflostaging.com">tracking.cityflostaging.com</option>
              <option value="custom">localhost</option>
            </select>
          </div>
          
          {localIsCustom && (
            <div className={styles.formGroup}>
              <label htmlFor="port" className={styles.label}>Port</label>
              <input
                type="text"
                id="port"
                className={styles.input}
                value={localPort}
                onChange={(e) => setLocalPort(e.target.value)}
                placeholder="Port (e.g. 8080)"
                maxLength={5}
              />
            </div>
          )}
          
          <div className={styles.buttonGroup}>
            <button type="button" className={styles.cancelButton} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className={styles.saveButton}>
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BackendSettingsModal;
